import type { TransformedTarget } from '../../../../api/targets-client/targets-client.type';
import { capitalizeFirstLetter } from '../../../../helpers/string-helpers/string-helpers';
import { graphLightRed, graphTropicalIndigo } from '../../../../styles/design-tokens';
import { MeasureDataType } from './targets-tab.type';

/**
 * Returns an array of all available measure data types.
 *
 * @returns {MeasureDataType[]} Array containing Actual and Target measure data types
 */
const getMeasureDataTypes = (): MeasureDataType[] => {
  return [MeasureDataType.Actual, MeasureDataType.Target];
};

/**
 * Returns a color mapping for measure data types.
 *
 * @returns {Record<MeasureDataType, string>} Object with measure data types as keys and colors as values
 */
const getMeasureDataTypesColors = (): Record<MeasureDataType, string> => ({
  [MeasureDataType.Actual]: graphTropicalIndigo,
  [MeasureDataType.Target]: graphLightRed,
});

/**
 * Returns a label for a measure data type.
 *
 * @param {TransformedTarget | null} target - The target object
 * @param {MeasureDataType} measureDataType - The measure data type
 * @returns {string | null} Label for the measure data type or null if target is not available
 */
const getMeasureDataTypesLabel = (
  target: TransformedTarget | null,
  measureDataType: MeasureDataType,
): string | null => {
  if (!target) {
    return null;
  }

  const measureLabel = capitalizeFirstLetter(target.measure);

  if (measureDataType === MeasureDataType.Actual) {
    return measureLabel;
  }

  return `Target ${measureLabel}`;
};

export { getMeasureDataTypes, getMeasureDataTypesColors, getMeasureDataTypesLabel };
